import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import EventosRtcModal from '../components/EventosRtcModal'
import { BotaoManual } from '../components/ManualBase'

interface NotaRow {
  emp: number
  chave: string
  serie: string
  numero: number
  cnpj_remetente: string
  nome_remetente: string
  emissao: string
  valor: string
  situacao: string
  digitada: boolean
  digitada_em: string | null
  xml_existe: boolean
  tpNF: string
  finNFe: string
  tpNFDebito: string
  tpNFCredito: string
  ciencia: boolean
  realiza: boolean
  desconhece: boolean
  nao_realizada: boolean
}

// cor da etiqueta do evento na janela "Eventos vinculados" (RTC 211xxx sem cor própria cai no azul)
const COR_EVENTO: Record<string, { bg: string; cor: string }> = {
  '210210': { bg: '#f3e8ff', cor: '#7e22ce' }, // Ciência
  '210200': { bg: '#dcfce7', cor: '#15803d' }, // Confirmação
  '210220': { bg: '#ffedd5', cor: '#c2410c' }, // Desconhecimento
  '210240': { bg: '#fee2e2', cor: '#b91c1c' }, // Não Realizada
  '211128': { bg: '#ccfbf1', cor: '#0f766e' }, // Aceite de débito
}

interface EventoNota {
  seq: number
  dataHora: string
  evento: string
  codEvento: string
  situacao: string
  sta: number | string
  motivo: string
  usuario: string
}

interface Filtros {
  periodoDe: string
  periodoAte: string
  situacao: string
  participante: string
  chave: string
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.82rem', height: 30, background: '#fff',
}

const label: React.CSSProperties = {
  fontSize: '0.72rem', color: '#6b7280', fontWeight: 600,
}

function campo(rotulo: string, children: React.ReactNode) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

// "Sim" ganha a cor do evento; "Não" fica neutro.
const CORES_SELO = {
  verde: { bg: '#dcfce7', fg: '#15803d' },
  vermelho: { bg: '#fee2e2', fg: '#dc2626' },
  azul: { bg: '#dbeafe', fg: '#1d4ed8' },
  roxo: { bg: '#ede9fe', fg: '#6d28d9' },
}
function selo(ativo: boolean, cor: keyof typeof CORES_SELO = 'verde') {
  const c = CORES_SELO[cor]
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
      fontSize: '0.74rem', fontWeight: 600,
      background: ativo ? c.bg : '#f1f5f9',
      color: ativo ? c.fg : '#94a3b8',
    }}>
      {ativo ? 'Sim' : 'Não'}
    </span>
  )
}

// Faixa à esquerda da linha pela situação da nota.
function corSituacao(n: { digitada: boolean; ciencia: boolean; realiza: boolean; desconhece: boolean; nao_realizada: boolean }) {
  if (n.desconhece || n.nao_realizada) return '#dc2626'
  if (n.digitada) return '#1d4ed8'
  if (n.realiza) return '#16a34a'
  if (n.ciencia) return '#7c3aed'
  return '#f59e0b'
}

const dividerStyle: React.CSSProperties = {
  width: 1, background: '#e2e8f0', alignSelf: 'stretch',
}

const thBase: React.CSSProperties = {
  padding: '8px 6px', whiteSpace: 'nowrap', color: '#fff', fontWeight: 700,
}

function stat(rotulo: string, valor: string | number, cor: string) {
  return (
    <div style={{ textAlign: 'center', minWidth: 90 }}>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {rotulo}
      </div>
      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </div>
    </div>
  )
}

function IconChave() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L21 7l-3-3m-3.5 3.5L17 4" />
    </svg>
  )
}

const SIT_BG: Record<string, string> = {
  Autorizada:   '#dcfce7',
  Denegada:     '#fee2e2',
  Cancelada:    '#f3f4f6',
  'A Consultar': '#fef3c7',
}
const SIT_COLOR: Record<string, string> = {
  Autorizada:   '#15803d',
  Denegada:     '#dc2626',
  Cancelada:    '#6b7280',
  'A Consultar': '#b45309',
}

function fmtMoeda(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtData(iso: string) {
  return iso.slice(0, 10).split('-').reverse().join('/')
}

function fmtDataHora(iso: string) {
  // ntv_xmldhd é "timestamp without time zone" — o node-pg lê o valor como se
  // fosse horário local e devolve serializado em UTC (soma 3h de diferença pro
  // Brasil). Fatiar a string direto mostrava esse UTC como se já fosse local;
  // convertendo com Date/toLocaleString ele volta pro horário de Brasília certo.
  const d = new Date(iso)
  const data = d.toLocaleDateString('pt-BR')
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${data} ${hora}`
}

function fmtCnpj(doc: string) {
  const n = (doc || '').replace(/\D/g, '').padStart(14, '0')
  return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

function ToolbarBtn({ title, bg, fg, border, children, disabled, destaque, onClick }: {
  title: string; bg: string; fg: string; border?: string; children: React.ReactNode; disabled?: boolean
  destaque?: boolean; onClick?: () => void
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `1px solid ${disabled ? '#e5e7eb' : destaque ? '#1a6ea8' : border ?? '#e2e8f0'}`,
        background: disabled ? '#f1f5f9' : destaque ? '#1a6ea8' : bg,
        color: disabled ? '#cbd5e1' : destaque ? '#fff' : fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: destaque ? '0 0 0 4px rgba(26,110,168,0.25), 0 2px 6px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.06)',
        transform: destaque ? 'scale(1.08)' : undefined,
        transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
      }}
    >
      {children}
    </button>
  )
}

const iconProps = {
  width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
}

function IconAtualizar() {
  return (
    <svg {...iconProps}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  )
}

function IconCheck() {
  return (
    <svg {...iconProps}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function IconConfirmar() {
  return (
    <svg {...iconProps}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}

function IconNaoRealizada() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  )
}

function IconDesconhecida() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12" y2="17.01" />
    </svg>
  )
}

function IconDownload() {
  return (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function IconImportar() {
  return (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 8 12 3 17 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function IconUploadXml() {
  return (
    <svg {...iconProps}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 18v-6M9 15l3-3 3 3" />
    </svg>
  )
}

function IconPdf() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

function IconDisponibilidade() {
  return (
    <svg {...iconProps}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  )
}

function filtroPadrao(): Filtros {
  const hoje = new Date()
  const inicio = new Date(hoje)
  inicio.setDate(inicio.getDate() - 7)
  return {
    periodoDe: inicio.toISOString().slice(0, 10),
    periodoAte: hoje.toISOString().slice(0, 10),
    situacao: '',
    participante: '',
    chave: '',
  }
}

// Vindo do painel de Compras: ?de=&ate=&situacao=&digitada=nao
const urlParams = () => new URLSearchParams(window.location.search)
function filtroInicial(): Filtros {
  const q = urlParams(), p = filtroPadrao()
  return { ...p, periodoDe: q.get('de') || p.periodoDe, periodoAte: q.get('ate') || p.periodoAte, situacao: q.get('situacao') ?? p.situacao }
}

export default function ManifestadorDestinatario() {
  const { empresaCod, empresaAtual } = useEmpresa()
  const navigate = useNavigate()
  const [filtros, setFiltros] = useState<Filtros>(filtroInicial)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroInicial)
  const [notas, setNotas] = useState<NotaRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const [chaveConsulta, setChaveConsulta] = useState('')
  const [filtroDigitada, setFiltroDigitada] = useState<'todas' | 'sim' | 'nao'>(() => urlParams().get('digitada') === 'nao' ? 'nao' : 'todas')
  const [atualizando, setAtualizando] = useState(false)
  const [msgAtualizar, setMsgAtualizar] = useState<{ texto: string; ok: boolean } | null>(null)
  const [manifestando, setManifestando] = useState(false)
  const [naoRealizadaModal, setNaoRealizadaModal] = useState(false)
  const [justificativa, setJustificativa] = useState('')
  const [erroJustificativa, setErroJustificativa] = useState<string | null>(null)
  const [eventosModal, setEventosModal] = useState<string | null>(null)
  const [rtcChave, setRtcChave] = useState<string | null>(null) // janela dos eventos da Reforma Tributária
  const [eventos, setEventos] = useState<EventoNota[]>([])
  const [carregandoEventos, setCarregandoEventos] = useState(false)
  const [erroEventos, setErroEventos] = useState<string | null>(null)

  async function abrirEventos(chave: string) {
    if (chave.length !== 44) {
      setMsgAtualizar({ texto: 'Informe a chave completa (44 dígitos) pra ver os eventos.', ok: false })
      return
    }
    setEventosModal(chave)
    setEventos([])
    setErroEventos(null)
    setCarregandoEventos(true)
    try {
      const r = await api.get(`/manifestador/${chave}/eventos`)
      setEventos(r.data)
    } catch (e: any) {
      setErroEventos(e?.response?.data?.error || 'Erro ao consultar eventos da NF-e')
    } finally {
      setCarregandoEventos(false)
    }
  }
  function fecharEventos() { setEventosModal(null) }

  // "Imprimir DANFE" gera o PDF direto do XML já capturado pela Distribuição
  // DF-e — não precisa a nota já estar digitada (igual o botão do ERP legado).
  function imprimirDanfeSelecionada() {
    if (selecionados.size !== 1) {
      setMsgAtualizar({ texto: 'Selecione exatamente uma nota pra imprimir o DANFE.', ok: false })
      return
    }
    const chave = [...selecionados][0]
    const nota = notas.find(n => n.chave === chave)
    if (!nota) return
    if (!nota.xml_existe) {
      setMsgAtualizar({ texto: 'O XML dessa nota ainda não foi baixado pela Distribuição DF-e.', ok: false })
      return
    }
    window.open(`/api/manifestador/${chave}/danfe`, '_blank')
  }

  // "Importar XML" só faz sentido pra uma nota de cada vez, ainda não digitada
  // e já Autorizada (mesma regra do Btn_Imp do Manifestador legado).
  function importarSelecionada() {
    if (selecionados.size !== 1) {
      setMsgAtualizar({ texto: 'Selecione exatamente uma nota (ainda não digitada) pra importar.', ok: false })
      return
    }
    const chave = [...selecionados][0]
    const nota = notas.find(n => n.chave === chave)
    if (!nota) return
    if (nota.digitada) {
      setMsgAtualizar({ texto: 'Essa nota já foi digitada — abra pela Consulta de Notas pra alterar.', ok: false })
      return
    }
    if (nota.situacao !== 'Autorizada') {
      setMsgAtualizar({ texto: 'Só é possível importar nota Autorizada.', ok: false })
      return
    }
    navigate(`/compras/recebimentos/lancamento-notas?importarChave=${chave}`)
  }

  // "Importar XML manual" — a Distribuição DF-e às vezes demora a entregar o XML
  // mesmo depois da Ciência da Operação, e às vezes não dá pra esperar. Sobe um
  // arquivo baixado manualmente (do portal da NF-e, e-mail etc.) direto pra
  // frota.ntvxml — mesma tabela da Distribuição DF-e — equivalente web do botão
  // de importação manual que já existe no Manifestador do Win (WManDes).
  const arquivoXmlRef = useRef<HTMLInputElement>(null)
  const [importandoManual, setImportandoManual] = useState(false)

  // Chave de acesso de dentro do XML — pra conferir contra a nota selecionada
  // antes de importar, igual o Win faz (evita subir o arquivo da nota errada).
  function extrairChaveXml(xml: string): string | null {
    return xml.match(/<infNFe[^>]*\bId="NFe(\d{44})"/)?.[1]
      ?? xml.match(/<chNFe>(\d{44})<\/chNFe>/)?.[1]
      ?? null
  }

  function abrirImportarXmlManual() {
    if (!notaSelecionadaUnica) {
      setMsgAtualizar({ texto: 'Selecione a nota antes de importar o XML manual.', ok: false })
      return
    }
    arquivoXmlRef.current?.click()
  }

  function aoSelecionarXmlManual(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0]
    e.target.value = ''
    if (!arquivo) return
    const notaAlvo = notaSelecionadaUnica
    if (!notaAlvo) {
      setMsgAtualizar({ texto: 'Selecione a nota antes de importar o XML manual.', ok: false })
      return
    }
    setImportandoManual(true)
    setMsgAtualizar(null)
    const leitor = new FileReader()
    leitor.onload = async () => {
      try {
        const xml = String(leitor.result || '')
        const chaveArquivo = extrairChaveXml(xml)
        if (chaveArquivo && chaveArquivo !== notaAlvo.chave) {
          setMsgAtualizar({
            texto: `Esse arquivo é da chave ${chaveArquivo}, diferente da nota selecionada (nº ${notaAlvo.numero}, chave ${notaAlvo.chave}). Confira se escolheu o XML certo.`,
            ok: false,
          })
          return
        }
        const r = await api.post('/erp/notas-entrada/importar-arquivo', { xml, empcod: empresaCod ?? 1, tipo: 'nfe' })
        navigate(`/compras/recebimentos/lancamento-notas?importarChave=${r.data.chave}`)
        return
      } catch (err: any) {
        setMsgAtualizar({ texto: err?.response?.data?.error || 'Erro ao importar o arquivo XML.', ok: false })
      } finally {
        setImportandoManual(false)
      }
    }
    leitor.onerror = () => {
      setMsgAtualizar({ texto: 'Não foi possível ler o arquivo selecionado.', ok: false })
      setImportandoManual(false)
    }
    leitor.readAsText(arquivo, 'utf-8')
  }

  const [consultandoStatus, setConsultandoStatus] = useState(false)

  async function consultarDisponibilidade() {
    if (consultandoStatus) return
    setConsultandoStatus(true)
    setMsgAtualizar(null)
    try {
      const r = await api.get('/nfe/status-servico')
      const d = r.data
      if (d.erro) {
        setMsgAtualizar({ texto: 'Não foi possível falar com a SEFAZ agora. Tente de novo em alguns instantes.', ok: false })
      } else if (d.emOperacao) {
        setMsgAtualizar({ texto: '✅ Servidor da Receita (RS) está funcionando normalmente. Pode emitir/transmitir sem problemas.', ok: true })
      } else if (d.cStat === '108' || d.cStat === '109') {
        setMsgAtualizar({ texto: '⚠ O servidor da Receita (RS) está fora do ar no momento. Aguarde um pouco e tente de novo.', ok: false })
      } else {
        setMsgAtualizar({ texto: `⚠ A Receita não confirmou que está tudo certo${d.xMotivo ? ` (${d.xMotivo})` : ''}. Tente de novo em alguns instantes.`, ok: false })
      }
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao consultar disponibilidade da SEFAZ.', ok: false })
    } finally {
      setConsultandoStatus(false)
    }
  }

  const carregar = useCallback(async () => {
    if (!empresaCod) return
    setCarregando(true)
    setErro(null)
    try {
      const params: Record<string, string> = { empCod: String(empresaCod) }
      if (pesquisaAtiva.periodoDe)    params.dataInicio   = pesquisaAtiva.periodoDe
      if (pesquisaAtiva.periodoAte)   params.dataFim      = pesquisaAtiva.periodoAte
      if (pesquisaAtiva.situacao)     params.situacao     = pesquisaAtiva.situacao
      if (pesquisaAtiva.participante) params.participante = pesquisaAtiva.participante
      if (pesquisaAtiva.chave)        params.chave        = pesquisaAtiva.chave
      const r = await api.get('/manifestador', { params })
      setNotas(r.data)
      setSelecionados(new Set())
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [empresaCod, pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p); setFiltroDigitada('todas') }

  // O bloqueio da consulta é do servidor (vale para todos os usuários e depois de F5): 1h20 depois de cada resposta da SEFAZ,
  // para não tomar o cStat 656. Só fica livre na hora quando a SEFAZ avisa que ainda há notas a buscar.
  const [bloqueioFim, setBloqueioFim] = useState<number | null>(null) // quando a consulta é liberada (ms, relógio deste computador)
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    if (!empresaCod) return
    api.get('/manifestador/bloqueio', { params: { empCod: empresaCod } })
      .then(r => setBloqueioFim(r.data.restanteSeg > 0 ? Date.now() + r.data.restanteSeg * 1000 : null))
      .catch(() => setBloqueioFim(null))
  }, [empresaCod])
  useEffect(() => {
    if (!bloqueioFim) return
    const t = setInterval(() => { setAgora(Date.now()); if (Date.now() >= bloqueioFim) setBloqueioFim(null) }, 1000)
    return () => clearInterval(t)
  }, [bloqueioFim])
  const restanteSeg = bloqueioFim ? Math.max(0, Math.ceil((bloqueioFim - agora) / 1000)) : 0
  const contagem = (s: number) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map(n => String(n).padStart(2, '0')).join(':')
  const liberaAs = bloqueioFim ? new Date(bloqueioFim).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''

  async function atualizarLista() {
    if (!empresaCod || atualizando || restanteSeg > 0) return
    setAtualizando(true)
    setMsgAtualizar(null)
    try {
      const r = await api.post('/manifestador/atualizar', { empCod: empresaCod })
      const res = r.data
      if (res.restanteSeg > 0) setBloqueioFim(Date.now() + res.restanteSeg * 1000)
      const hora = res.restanteSeg > 0 ? new Date(Date.now() + res.restanteSeg * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''
      const sufixo = res.erro ? '' : res.temMais ? ' Ainda há notas a buscar: clique em Atualizar Lista de novo.'
        : hora ? ` A próxima consulta fica liberada às ${hora}, para evitar o limite da SEFAZ.` : ''
      if (res.erro) {
        setMsgAtualizar({ texto: res.erro, ok: false })
      } else if (res.cStat === '138') {
        setMsgAtualizar({ texto: `${res.novas} nota(s) nova(s) encontrada(s).${sufixo}`, ok: true })
      } else if (res.cStat === '137') {
        setMsgAtualizar({ texto: `Nenhum documento novo encontrado.${sufixo}`, ok: true })
      } else if (res.cStat === '656') {
        setMsgAtualizar({ texto: `SEFAZ rejeitou por consumo indevido (cStat 656) — aguarde antes de consultar de novo.${sufixo}`, ok: false })
      } else {
        setMsgAtualizar({ texto: res.cStat ? `cStat ${res.cStat}: ${res.xMotivo}${sufixo}` : 'Sem resposta da SEFAZ', ok: false })
      }
      await carregar()
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao atualizar lista', ok: false })
    } finally {
      setAtualizando(false)
    }
  }

  const TIPO_LABEL: Record<string, string> = {
    confirma: 'Confirmação da Operação',
    ciencia: 'Ciência da Operação',
    desconhece: 'Operação Desconhecida',
    naoRealizada: 'Operação Não Realizada',
  }

  async function manifestar(tipo: 'confirma' | 'ciencia' | 'desconhece' | 'naoRealizada', justif?: string) {
    if (selecionados.size === 0) {
      setMsgAtualizar({ texto: 'Selecione ao menos uma nota para manifestar.', ok: false })
      return
    }
    const selecionadas = [...selecionados].map(ch => notas.find(n => n.chave === ch)).filter((n): n is NotaRow => !!n)
    const validas = selecionadas.filter(n => n.situacao === 'Autorizada')
    const ignoradas = selecionadas.length - validas.length
    if (validas.length === 0) {
      setMsgAtualizar({ texto: 'Nenhuma das notas selecionadas está Autorizada.', ok: false })
      return
    }
    const confirmMsg = validas.length === 1
      ? `Confirma o envio de "${TIPO_LABEL[tipo]}" para a NF-e nº ${validas[0].numero}? Essa ação é transmitida direto à SEFAZ e não pode ser desfeita.`
      : `Confirma o envio de "${TIPO_LABEL[tipo]}" para ${validas.length} nota(s)${ignoradas ? ` (${ignoradas} não Autorizada(s) serão ignoradas)` : ''}? Essa ação é transmitida direto à SEFAZ, uma nota de cada vez, e não pode ser desfeita.`
    if (!window.confirm(confirmMsg)) return

    setManifestando(true)
    setMsgAtualizar(null)
    let sucesso = 0
    const falhas: string[] = []
    for (const nota of validas) {
      try {
        const r = await api.post(`/manifestador/${nota.chave}/manifestar`, { empCod: empresaCod, tipo, justificativa: justif })
        const res = r.data
        if (res.registrada) sucesso++
        else falhas.push(`nº ${nota.numero}: ${res.cStat ? `cStat ${res.cStat} ${res.xMotivo}` : res.erro || 'sem resposta'}`)
      } catch (e: any) {
        falhas.push(`nº ${nota.numero}: ${e?.response?.data?.error || 'erro ao registrar'}`)
      }
    }
    const texto = validas.length === 1
      ? (sucesso === 1 ? `${TIPO_LABEL[tipo]} registrada` : falhas[0])
      : `${sucesso} de ${validas.length} nota(s) manifestada(s) com sucesso.${falhas.length ? ' Falhas — ' + falhas.join('; ') : ''}`
    setMsgAtualizar({ texto, ok: falhas.length === 0 })
    await carregar()
    setManifestando(false)
  }

  function abrirNaoRealizada() {
    if (selecionados.size === 0) {
      setMsgAtualizar({ texto: 'Selecione ao menos uma nota para manifestar.', ok: false })
      return
    }
    setJustificativa('')
    setErroJustificativa(null)
    setNaoRealizadaModal(true)
  }

  function fecharNaoRealizada() {
    if (manifestando) return
    setNaoRealizadaModal(false)
  }

  async function confirmarNaoRealizada() {
    if (justificativa.trim().length < 15) {
      setErroJustificativa('Justificativa deve ter no mínimo 15 caracteres')
      return
    }
    setNaoRealizadaModal(false)
    await manifestar('naoRealizada', justificativa.trim())
  }

  function toggleSel(chave: string) {
    setSelecionados(s => {
      const novo = new Set(s)
      if (novo.has(chave)) novo.delete(chave); else novo.add(chave)
      return novo
    })
  }

  // Marcou só uma nota: já joga a chave no campo de baixo, pra poder copiar.
  useEffect(() => {
    setChaveConsulta(selecionados.size === 1 ? [...selecionados][0] : '')
  }, [selecionados])

  const notaSelecionadaUnica = selecionados.size === 1 ? notas.find(n => n.chave === [...selecionados][0]) : null
  const podeImportar = !!notaSelecionadaUnica && !notaSelecionadaUnica.digitada && notaSelecionadaUnica.situacao === 'Autorizada'
  const podeImprimirDanfe = !!notaSelecionadaUnica && notaSelecionadaUnica.xml_existe

  const notasFiltradas = notas.filter(n =>
    filtroDigitada === 'todas' ? true : filtroDigitada === 'sim' ? n.digitada : !n.digitada
  )

  const digitadas = notas.filter(n => n.digitada)
  const aDigitar = notas.filter(n => !n.digitada)
  const totalDigitadas = digitadas.reduce((s, n) => s + parseFloat(n.valor), 0)
  const totalADigitar = aDigitar.reduce((s, n) => s + parseFloat(n.valor), 0)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 0 0.75rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
          Manifestador do Destinatário
        </h2>
        <BotaoManual para="/compras/ajuda/manual-manifestador-nfe" titulo="Abrir o manual do Manifestador de NF-e" />
      </div>

      {/* Filtros */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        padding: '0.75rem 0.9rem', marginBottom: '1rem',
      }}>
        {campo('EMPRESA', (
          <div style={{ ...inp, width: 220, display: 'flex', alignItems: 'center', background: '#f3f4f6', color: '#6b7280' }}>
            {empresaAtual ? `${empresaAtual.empcod} - ${empresaAtual.empfan || empresaAtual.empnom}` : '—'}
          </div>
        ))}
        {campo('PERÍODO EMISSÃO DE', <input style={{ ...inp, width: 140 }} type="date" value={filtros.periodoDe}
          onChange={e => setFiltros(f => ({ ...f, periodoDe: e.target.value }))} />)}
        {campo('ATÉ', <input style={{ ...inp, width: 140 }} type="date" value={filtros.periodoAte}
          onChange={e => setFiltros(f => ({ ...f, periodoAte: e.target.value }))} />)}
        {campo('SITUAÇÃO', (
          <select style={{ ...inp, width: 130 }} value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}>
            <option value="">Todas</option>
            <option value="1">Autorizada</option>
            <option value="2">Denegada</option>
            <option value="3">Cancelada</option>
          </select>
        ))}
        {campo('DIGITADA', (
          <select style={{ ...inp, width: 130 }} value={filtroDigitada}
            onChange={e => setFiltroDigitada(e.target.value as 'todas' | 'sim' | 'nao')}>
            <option value="todas">Todas</option>
            <option value="sim">Digitadas</option>
            <option value="nao">Não digitadas</option>
          </select>
        ))}
        {campo('PARTICIPANTE (CNPJ)', (
          <input style={{ ...inp, width: 160 }} value={filtros.participante}
            placeholder="CNPJ do remetente"
            onChange={e => setFiltros(f => ({ ...f, participante: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        {campo('CHAVE OU Nº DA NF-E', (
          <input style={{ ...inp, width: 340 }} value={filtros.chave}
            placeholder="Chave de 44 dígitos ou só o número"
            onChange={e => setFiltros(f => ({ ...f, chave: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
          style={{ height: 30, alignSelf: 'flex-end' }}>
          {carregando ? 'Buscando...' : 'Pesquisar'}
        </button>
        <button className="btn btn-sm" onClick={limpar} style={{ height: 30, alignSelf: 'flex-end', color: '#6b7280' }}>
          Limpar
        </button>
        {podeImprimirDanfe && (
          <button
            className="btn btn-danger-solid btn-sm"
            onClick={imprimirDanfeSelecionada}
            style={{ height: 30, alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconPdf /> Imprimir DANFE
          </button>
        )}
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {msgAtualizar && (
        <div className={`alert ${msgAtualizar.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {msgAtualizar.texto}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
        <div style={{ flex: '0 1 auto', minWidth: 0 }}>
          {/* Grid */}
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table className="table" style={{ fontSize: '0.78rem', minWidth: 1100, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1a6ea8' }}>
                  <th style={{ ...thBase, width: 30 }}></th>
                  <th style={{ ...thBase, width: 40, textAlign: 'center' }}>Emp</th>
                  <th style={{ ...thBase, width: 40, textAlign: 'center' }}>Ser</th>
                  <th style={{ ...thBase, width: 80 }}>Número</th>
                  <th style={{ ...thBase, width: 130 }}>Participante</th>
                  <th style={{ ...thBase }}>Nome Participante</th>
                  <th style={{ ...thBase, width: 80 }}>Emissão</th>
                  <th style={{ ...thBase, width: 100, textAlign: 'right' }}>Valor R$</th>
                  <th style={{ ...thBase, width: 100 }}>Situação</th>
                  <th style={{ ...thBase, width: 70, textAlign: 'center' }}>Tipo NF</th>
                  <th style={{ ...thBase, width: 100 }}>Finalidade</th>
                  <th style={{ ...thBase, width: 100 }} title="Tipo Débito/Crédito">Deb/Cred</th>
                  <th style={{ ...thBase, width: 82, textAlign: 'center' }}>Dig Fro</th>
                  <th style={{ ...thBase, width: 130 }}>Digitada em</th>
                  <th style={{ ...thBase, width: 70, textAlign: 'center' }}>Ciência</th>
                  <th style={{ ...thBase, width: 70, textAlign: 'center' }}>Realiza</th>
                  <th style={{ ...thBase, width: 70, textAlign: 'center' }}>Desco.</th>
                  <th style={{ ...thBase, width: 82, textAlign: 'center' }}>N Real</th>
                  <th style={{ ...thBase, width: 50, textAlign: 'center' }}>XML</th>
                </tr>
              </thead>
              <tbody>
                {carregando && (
                  <tr><td colSpan={19} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
                )}
                {!carregando && notasFiltradas.length === 0 && (
                  <tr><td colSpan={19} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma nota encontrada</td></tr>
                )}
                {notasFiltradas.map((n, idx) => (
                  <tr key={n.chave}
                    onDoubleClick={() => abrirEventos(n.chave)}
                    title="Duplo clique para ver os eventos já enviados dessa NF-e"
                    style={{ borderBottom: '1px solid #d5dfeb', background: selecionados.has(n.chave) ? '#cfe6fb' : idx % 2 === 1 ? '#edf3fa' : '#fff', cursor: 'pointer' }}>
                    <td style={{ textAlign: 'center', padding: '6px', borderLeft: `4px solid ${corSituacao(n)}` }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selecionados.has(n.chave)} onChange={() => toggleSel(n.chave)} />
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{n.emp}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{n.serie}</td>
                    <td style={{ fontWeight: 700, padding: '6px' }}>
                      {n.digitada ? (
                        <span onClick={() => navigate(`/compras/consultas/notas?numero=${n.numero}`)}
                          style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                          title="Já digitada — clique para consultar a nota">
                          {n.numero}
                        </span>
                      ) : n.numero}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.74rem', whiteSpace: 'nowrap', padding: '6px' }}>{fmtCnpj(n.cnpj_remetente)}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px' }} title={n.nome_remetente}>
                      {n.nome_remetente}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', padding: '6px' }}>{fmtData(n.emissao)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, padding: '6px' }}>{fmtMoeda(n.valor)}</td>
                    <td style={{ padding: '6px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                        fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap',
                        background: SIT_BG[n.situacao] ?? '#f3f4f6',
                        color: SIT_COLOR[n.situacao] ?? '#374151',
                      }}>
                        {n.situacao}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{n.tpNF || '-'}</td>
                    <td style={{ padding: '6px' }}>{n.finNFe || '-'}</td>
                    <td style={{ padding: '6px' }}>{n.tpNFDebito || n.tpNFCredito || ''}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.digitada, 'azul')}</td>
                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280', padding: '6px' }}>{n.digitada && n.digitada_em ? fmtDataHora(n.digitada_em) : ''}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.ciencia, 'roxo')}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.realiza, 'verde')}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.desconhece, 'vermelho')}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.nao_realizada, 'vermelho')}</td>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{selo(n.xml_existe, 'verde')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Resumo */}
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap', gap: '1.75rem',
            border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px',
            background: '#f8fafc', padding: '0.85rem 1.25rem',
          }}>
            {stat('NFs Digitadas', digitadas.length, '#2563eb')}
            <div style={dividerStyle} />
            {stat('Total Digitadas R$', fmtMoeda(totalDigitadas), '#2563eb')}
            <div style={dividerStyle} />
            {stat('NFs a Digitar', aDigitar.length, aDigitar.length > 0 ? '#b45309' : '#0f172a')}
            <div style={dividerStyle} />
            {stat('Total a Digitar R$', fmtMoeda(totalADigitar), aDigitar.length > 0 ? '#b45309' : '#0f172a')}
          </div>

          {/* Chave da NF-e */}
          <div style={{
            marginTop: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 8,
            background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            padding: '0.9rem 1rem', textAlign: 'center',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <IconChave />
              <input
                style={{ ...inp, width: 400, fontFamily: 'monospace' }}
                value={chaveConsulta}
                onChange={e => setChaveConsulta(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && abrirEventos(chaveConsulta.replace(/\D/g, ''))}
                placeholder="Chave de acesso da NF-e (44 dígitos)"
              />
            </div>
            <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
              Tecle{' '}
              <span style={{
                display: 'inline-block', padding: '1px 7px', borderRadius: 4,
                background: '#f1f5f9', border: '1px solid #e2e8f0',
                fontFamily: 'monospace', fontWeight: 700, color: '#475569', fontSize: '0.72rem',
              }}>
                Enter ↵
              </span>{' '}
              para visualizar os eventos da NF-e
            </span>
          </div>
        </div>

        {/* Barra de ações */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '0.6rem',
          padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
        }}>
          <ToolbarBtn
            title={atualizando ? 'Consultando SEFAZ...'
              : restanteSeg > 0 ? `Atualizar Lista bloqueado para evitar o limite da SEFAZ (cStat 656). Libera às ${liberaAs} (faltam ${contagem(restanteSeg)})`
              : 'Atualizar Lista'}
            bg="#334155" fg="#fff" border="#334155"
            disabled={atualizando || restanteSeg > 0}
            onClick={atualizarLista}
          >
            <IconAtualizar />
          </ToolbarBtn>
          {restanteSeg > 0 && (
            <div title={`Atualizar Lista fica liberado às ${liberaAs}`}
              style={{ marginTop: '-0.3rem', fontSize: '0.62rem', fontWeight: 800, textAlign: 'center', color: '#b45309', fontVariantNumeric: 'tabular-nums' }}>
              {contagem(restanteSeg)}
            </div>
          )}
          <ToolbarBtn
            title={manifestando ? 'Enviando...' : 'Ciência da Operação'}
            bg="#15803d" fg="#fff" border="#15803d" disabled={manifestando}
            onClick={() => manifestar('ciencia')}
          ><IconCheck /></ToolbarBtn>
          <ToolbarBtn
            title={manifestando ? 'Enviando...' : 'Confirmar Operação'}
            bg="#334155" fg="#fff" border="#334155" disabled={manifestando}
            onClick={() => manifestar('confirma')}
          ><IconConfirmar /></ToolbarBtn>
          <ToolbarBtn
            title={manifestando ? 'Enviando...' : 'Operação Não Realizada'}
            bg="#b91c1c" fg="#fff" border="#b91c1c" disabled={manifestando}
            onClick={abrirNaoRealizada}
          ><IconNaoRealizada /></ToolbarBtn>
          <ToolbarBtn
            title={manifestando ? 'Enviando...' : 'Operação Desconhecida'}
            bg="#334155" fg="#fff" border="#334155" disabled={manifestando}
            onClick={() => manifestar('desconhece')}
          ><IconDesconhecida /></ToolbarBtn>
          <ToolbarBtn
            title="Abrir o portal da NF-e pra consultar/baixar o XML pela chave"
            bg="#334155" fg="#fff" border="#334155"
            onClick={() => window.open('https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=completa&tipoConteudo=XbSeqxE8pl8=', '_blank')}
          ><IconDownload /></ToolbarBtn>
          <ToolbarBtn
            title={podeImportar ? 'Importar XML pro Lançamento de Notas' : 'Selecione uma nota Autorizada e ainda não digitada'}
            bg="#334155" fg="#fff" border="#334155" destaque={podeImportar}
            onClick={importarSelecionada}
          ><IconImportar /></ToolbarBtn>
          <input ref={arquivoXmlRef} type="file" accept=".xml" onChange={aoSelecionarXmlManual} style={{ display: 'none' }} />
          <ToolbarBtn
            title={
              importandoManual ? 'Importando...'
                : notaSelecionadaUnica ? `Importar arquivo XML da nota nº ${notaSelecionadaUnica.numero} (baixado manualmente)`
                : 'Selecione uma nota pra importar o XML manual'
            }
            bg="#334155" fg="#fff" border="#334155" disabled={importandoManual || !notaSelecionadaUnica}
            onClick={abrirImportarXmlManual}
          ><IconUploadXml /></ToolbarBtn>
          <ToolbarBtn
            title={notaSelecionadaUnica ? `Eventos da Reforma Tributária (IBS/CBS) — NF-e nº ${notaSelecionadaUnica.numero}` : 'Selecione uma nota pra enviar eventos da Reforma Tributária (IBS/CBS)'}
            bg="#334155" fg="#fff" border="#334155" disabled={!notaSelecionadaUnica}
            onClick={() => notaSelecionadaUnica && setRtcChave(notaSelecionadaUnica.chave)}
          ><span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.02em' }}>RTC</span></ToolbarBtn>
          <ToolbarBtn
            title={consultandoStatus ? 'Consultando SEFAZ-RS...' : 'Consulta Disponibilidade (SEFAZ-RS)'}
            bg="#334155" fg="#fff" border="#334155" disabled={consultandoStatus}
            onClick={consultarDisponibilidade}
          ><IconDisponibilidade /></ToolbarBtn>
        </div>
      </div>

      {naoRealizadaModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={fecharNaoRealizada}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#b91c1c' }}>
              Operação Não Realizada
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#6b7280' }}>
              {selecionados.size === 1
                ? `NF-e nº ${notaSelecionadaUnica?.numero}`
                : `${selecionados.size} nota(s) selecionada(s)`}
            </p>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Justificativa (mín. 15 caracteres)
            </label>
            <textarea
              autoFocus value={justificativa}
              onChange={e => { setJustificativa(e.target.value); setErroJustificativa(null) }}
              rows={4} maxLength={255}
              placeholder="Informe o motivo da operação não realizada..."
              style={{
                width: '100%', boxSizing: 'border-box',
                border: `1px solid ${erroJustificativa ? '#fca5a5' : '#d1d5db'}`,
                borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem', resize: 'vertical',
              }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontSize: '0.75rem', color: erroJustificativa ? '#dc2626' : '#9ca3af' }}>
                {erroJustificativa ?? `${justificativa.length}/255`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-sm" onClick={fecharNaoRealizada} disabled={manifestando} style={{ color: '#6b7280' }}>
                Voltar
              </button>
              <button onClick={confirmarNaoRealizada}
                disabled={manifestando || justificativa.trim().length < 15}
                style={{
                  background: '#b91c1c', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '5px 16px', fontSize: '0.82rem',
                  fontWeight: 600, cursor: manifestando ? 'not-allowed' : 'pointer',
                  opacity: manifestando || justificativa.trim().length < 15 ? 0.6 : 1,
                }}>
                {manifestando ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {eventosModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={fecharEventos}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 720, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8' }}>
              Eventos vinculados à NF-e
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: '#6b7280', fontFamily: 'monospace' }}>
              {eventosModal}
            </p>
            {carregandoEventos && <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>Carregando...</div>}
            {erroEventos && <div className="alert alert-danger">{erroEventos}</div>}
            {!carregandoEventos && !erroEventos && (
              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ fontSize: '0.78rem', width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#1a6ea8' }}>
                      <th style={{ ...thBase, width: 40, textAlign: 'center' }}>Seq</th>
                      <th style={{ ...thBase, width: 130 }}>Data e Hora</th>
                      <th style={thBase}>Evento</th>
                      <th style={{ ...thBase, width: 90, textAlign: 'center' }}>Situação</th>
                      <th style={thBase}>Justificativa/Motivo SEFAZ</th>
                      <th style={{ ...thBase, width: 90 }}>Usuário</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventos.length === 0 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>Nenhum evento enviado ainda pra essa NF-e</td></tr>
                    )}
                    {eventos.map(ev => (
                      <tr key={ev.seq} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ textAlign: 'center', padding: '6px' }}>{ev.seq}</td>
                        <td style={{ whiteSpace: 'nowrap', padding: '6px' }}>{fmtDataHora(ev.dataHora)}</td>
                        <td style={{ padding: '6px' }}>
                          {(() => {
                            const c = COR_EVENTO[ev.codEvento] ?? (ev.codEvento?.startsWith('211') ? { bg: '#e0f2fe', cor: '#0369a1' } : { bg: '#f1f5f9', cor: '#475569' })
                            return (
                              <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 6, fontWeight: 700, fontSize: '0.74rem', background: c.bg, color: c.cor, borderLeft: `3px solid ${c.cor}` }}>
                                {ev.evento}
                              </span>
                            )
                          })()}
                        </td>
                        <td style={{ textAlign: 'center', padding: '6px' }}>
                          <span style={{
                            display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                            fontSize: '0.72rem', fontWeight: 600,
                            background: ev.situacao === 'Homologado' ? '#dcfce7' : '#fee2e2',
                            color: ev.situacao === 'Homologado' ? '#15803d' : '#dc2626',
                          }}>
                            {ev.situacao} ({ev.sta})
                          </span>
                        </td>
                        <td style={{ padding: '6px' }}>{ev.motivo}</td>
                        <td style={{ padding: '6px' }}>{ev.usuario}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-sm" onClick={fecharEventos} style={{ color: '#6b7280' }}>Fechar</button>
            </div>
          </div>
        </div>
      )}
      {rtcChave && <EventosRtcModal chave={rtcChave} empCod={empresaCod ?? 1} onClose={() => setRtcChave(null)} />}
    </div>
  )
}
